import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'
import api from '../services/api'

interface User {
  id: string
  email: string
  name: string
  is_admin: boolean
  is_active: boolean
  first_login: boolean
  created_at: string
  updated_at: string
  last_login: string | null
  is_impersonating?: boolean
  impersonator_name?: string
}

interface AuthContextType {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => void
  changePassword: (oldPassword: string | null, newPassword: string) => Promise<void>
  impersonateUser: (userId: string) => Promise<void>
  exitImpersonation: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

interface AuthProviderProps {
  children: ReactNode
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const navigate = useNavigate()

  useEffect(() => {
    // Check if user is logged in on mount
    const token = localStorage.getItem('token')
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`
      fetchCurrentUser()
    } else {
      setLoading(false)
    }
  }, [])

  const fetchCurrentUser = async () => {
    try {
      const response = await api.get('/auth/me')
      setUser(response.data)
    } catch (error) {
      console.error('Failed to fetch current user:', error)
      localStorage.removeItem('token')
      delete api.defaults.headers.common['Authorization']
    } finally {
      setLoading(false)
    }
  }

  const login = async (email: string, password: string) => {
    const formData = new FormData()
    formData.append('username', email)
    formData.append('password', password)

    const response = await api.post('/auth/login', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    })

    const { access_token, user } = response.data
    localStorage.setItem('token', access_token)
    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
    setUser(user)

    // Redirect based on first login
    if (user.first_login) {
      navigate('/change-password')
    } else {
      navigate('/projects')
    }
  }

  const logout = () => {
    localStorage.removeItem('token')
    delete api.defaults.headers.common['Authorization']
    setUser(null)
    navigate('/login')
  }

  const changePassword = async (oldPassword: string | null, newPassword: string) => {
    await api.post('/auth/change-password', {
      old_password: oldPassword,
      new_password: newPassword,
    })

    // If it was first login, redirect to projects
    if (user?.first_login) {
      await fetchCurrentUser() // Refresh user data
      navigate('/projects')
    }
  }

  const impersonateUser = async (userId: string) => {
    const response = await api.post(`/auth/impersonate/${userId}`)
    const { access_token, user } = response.data
    localStorage.setItem('token', access_token)
    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`

    // Add impersonation flag to user data
    const userWithImpersonation = {
      ...user,
      is_impersonating: true,
    }
    setUser(userWithImpersonation)
    navigate('/projects')
  }

  const exitImpersonation = async () => {
    const response = await api.post('/auth/exit-impersonation')
    const { access_token, user } = response.data
    localStorage.setItem('token', access_token)
    api.defaults.headers.common['Authorization'] = `Bearer ${access_token}`
    setUser(user)
    navigate('/users')
  }

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout, changePassword, impersonateUser, exitImpersonation }}
    >
      {children}
    </AuthContext.Provider>
  )
}
